/**
 * Autenticacao — `POST /v1/auth/*` (`API §6.1`) e `POST /v1/auth/staff/*`.
 *
 * **Nao ha nenhuma linha que guarde nada.** O `verifyOtp` responde e o servidor manda
 * `Set-Cookie: __Host-session=...`; o navegador guarda, e acabou. Nao ha `accessToken` no
 * corpo (`VerifyOtpResponseSchema` nao o tem), nao ha `localStorage`, nao ha `sessionStorage`
 * e nao ha IndexedDB (ADR-0009: *"Nada de token em `localStorage`, `sessionStorage` ou
 * IndexedDB — resolve C-07 e o §30, e e verificado por teste automatizado que inspeciona o
 * storage apos login"*).
 *
 * O que o `logout` faz de mais e chamar `onSessionLost`? **Nao** — quem chama e o motor, e so
 * quando o servidor recusa por sessao. O logout deliberado e da aplicacao: `PWA §7.5` da a ela
 * sete passos (chaves, stores, caches, push, `BroadcastChannel`, `postMessage` ao SW,
 * `localStorage`), e nenhum deles e do cliente HTTP. O que este pacote oferece e
 * `resetSessionState()`, que apaga o que **ele** guarda.
 */
import {
  LogoutResponseSchema,
  RefreshSessionResponseSchema,
  RequestOtpResponseSchema,
  ShiftEndResponseSchema,
  ShiftStartResponseSchema,
  StaffLoginResponseSchema,
  VerifyOtpResponseSchema,
  type LogoutRequest,
  type LogoutResponse,
  type RefreshSessionResponse,
  type RequestOtpRequest,
  type RequestOtpResponse,
  type ShiftEndResponse,
  type ShiftStartRequest,
  type ShiftStartResponse,
  type StaffLoginRequest,
  type StaffLoginResponse,
  type VerifyOtpRequest,
  type VerifyOtpResponse,
} from '@farmacia/contracts';

import type { ApiClientCore } from '../core.js';

export interface AuthApi {
  /** `POST /v1/auth/otp/request` — `202`. Rate limit de 3/15 min por telefone (`API §9`). */
  requestOtp: (body: RequestOtpRequest) => Promise<RequestOtpResponse>;
  /** `POST /v1/auth/otp/verify`. A sessao chega em cookie; o corpo nao traz token. */
  verifyOtp: (body: VerifyOtpRequest) => Promise<VerifyOtpResponse>;
  /** `POST /v1/auth/refresh` — rotaciona o token opaco (mesma linha de sessao). */
  refresh: () => Promise<RefreshSessionResponse>;
  logout: (body?: LogoutRequest) => Promise<LogoutResponse>;
  staff: {
    login: (body: StaffLoginRequest) => Promise<StaffLoginResponse>;
    startShift: (body: ShiftStartRequest) => Promise<ShiftStartResponse>;
    endShift: () => Promise<ShiftEndResponse>;
  };
}

export function createAuthApi(core: ApiClientCore): AuthApi {
  return {
    async requestOtp(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/auth/otp/request',
        body,
        response: RequestOtpResponseSchema,
      });
      return data;
    },

    async verifyOtp(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/auth/otp/verify',
        body,
        response: VerifyOtpResponseSchema,
      });
      return data;
    },

    async refresh() {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/auth/refresh',
        body: {},
        response: RefreshSessionResponseSchema,
      });
      return data;
    },

    async logout(body) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/auth/logout',
        body: body ?? {},
        response: LogoutResponseSchema,
      });
      return data;
    },

    staff: {
      async login(body) {
        const { data } = await core.request({
          method: 'POST',
          path: '/v1/auth/staff/login',
          body,
          response: StaffLoginResponseSchema,
        });
        return data;
      },

      async startShift(body) {
        const { data } = await core.request({
          method: 'POST',
          path: '/v1/auth/staff/shift/start',
          body,
          response: ShiftStartResponseSchema,
        });
        return data;
      },

      async endShift() {
        const { data } = await core.request({
          method: 'POST',
          path: '/v1/auth/staff/shift/end',
          body: {},
          response: ShiftEndResponseSchema,
        });
        return data;
      },
    },
  };
}
